/**
 * Performance Dashboard — Regime scanner signal performance
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Skeleton, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  TrendingDown, TrendingUp, ShowChart, Assessment, BarChart,
  Check, Close, KeyboardArrowDown, KeyboardArrowRight, Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { performanceAPI } from '../services/api';

// ── Helpers ──
const fmtPct = (v, d = 1) => v != null ? `${v > 0 ? '+' : ''}${v.toFixed(d)}%` : '—';
const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

const SIGNAL_TYPES = { DISTRIBUTION: 'DISTRIBUTION', ACCUMULATION: 'ACCUMULATION' };

// ── Empty State ──
const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: '#cbd5e1' }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 400 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// ── Skeleton ──
const DashboardSkeleton = () => (
  <Box>
    {/* Tiles */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
      {[0,1,2,3].map(i => (
        <Box key={i} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2 }} />
            <Box sx={{ flex: 1 }}><Skeleton width={60} height={12} sx={{ mb: 0.8 }} /><Skeleton width={45} height={26} /></Box>
          </Box>
        </Box>
      ))}
    </Box>
    {/* Tabs placeholder */}
    <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
      {[0,1,2].map(i => (
        <Box key={i} sx={{ px: 2.5, py: 1.2 }}><Skeleton width={80} height={16} /></Box>
      ))}
    </Box>
    {/* Content */}
    <Box sx={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 10px 10px', p: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, mb: 2 }}>
        {[0,1,2,3].map(i => (
          <Box key={i} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', p: 1.5 }}>
            <Skeleton width={70} height={12} sx={{ mb: 1 }} />
            {[0,1,2].map(j => (
              <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                <Skeleton width={60} height={14} />
                <Skeleton width={40} height={14} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

// ── Win Rate Bar ──
const WinBar = ({ label, value, primary }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: primary ? 700 : 500, color: '#64748b' }}>{label}</Typography>
        {primary && <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#059669', border: '1px solid #059669', borderRadius: 4, px: 0.5, py: 0.1, lineHeight: 1 }}>Primary</Typography>}
      </Box>
      <Typography sx={{ fontSize: primary ? '1rem' : '0.88rem', fontWeight: 800, color: value >= 50 ? '#10b981' : '#ef4444', fontFamily: 'monospace' }}>{value?.toFixed(0) || '—'}%</Typography>
    </Box>
    <Box sx={{ height: 5, bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ width: `${value || 0}%`, height: '100%', bgcolor: value >= 50 ? '#10b981' : '#ef4444', borderRadius: 1, transition: 'width 0.5s' }} />
    </Box>
  </Box>
);

// ── Metric Row ──
const MetricRow = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.4 }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color, fontFamily: 'monospace' }}>{value}</Typography>
  </Box>
);

// ── Detailed Metrics ──
const DetailedMetrics = ({ metrics, signalType, t }) => {
  if (!metrics || metrics.total_signals === 0) {
    return <EmptyState Icon={Assessment} title={t('performance.noSignals', { type: signalType.toLowerCase() })} />;
  }
  const { win_rate_1w, win_rate_4w, win_rate_8w, avg_return_1w, avg_return_4w, avg_return_8w, avg_winner_return, avg_loser_return, avg_max_gain, avg_max_loss, risk_reward_ratio, big_winners, big_losers, total_signals } = metrics;
  const isDist = signalType === SIGNAL_TYPES.DISTRIBUTION;
  const retClr = v => v < 0 ? (isDist ? '#10b981' : '#ef4444') : (isDist ? '#ef4444' : '#10b981');

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.4fr 1.1fr', gap: 1.5, p: 2 }}>
      {/* Win Rates */}
      <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#3b82f6', 0.15)}`, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.2 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShowChart sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{t('performance.metrics.winRates')}</Typography>
        </Box>
        <WinBar label={t('performance.metrics.oneWeek')} value={win_rate_1w} />
        <WinBar label={t('performance.metrics.fourWeeks')} value={win_rate_4w} primary />
        <WinBar label={t('performance.metrics.eightWeeks')} value={win_rate_8w} />
      </Box>

      {/* Avg Returns */}
      <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#f59e0b', 0.15)}`, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.2 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUp sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{t('performance.metrics.avgReturns')}</Typography>
        </Box>
        <MetricRow label={t('performance.metrics.oneWeek')} value={fmtPct(avg_return_1w)} color={retClr(avg_return_1w)} />
        <MetricRow label={t('performance.metrics.fourWeeks')} value={fmtPct(avg_return_4w)} color={retClr(avg_return_4w)} />
        <MetricRow label={t('performance.metrics.eightWeeks')} value={fmtPct(avg_return_8w)} color={retClr(avg_return_8w)} />
      </Box>

      {/* Risk Metrics */}
      <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#ec4899', 0.15)}`, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.2 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Assessment sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{t('performance.metrics.riskMetrics')}</Typography>
        </Box>
        <MetricRow label={t('performance.metrics.avgWinner')} value={fmtPct(avg_winner_return)} color="#10b981" />
        <MetricRow label={t('performance.metrics.avgLoser')} value={fmtPct(avg_loser_return)} color="#ef4444" />
        <MetricRow label={t('performance.metrics.maxGain')} value={fmtPct(avg_max_gain)} color="#10b981" />
        <MetricRow label={t('performance.metrics.maxLoss')} value={fmtPct(avg_max_loss)} color="#ef4444" />
        <Box sx={{ pt: 0.8, mt: 0.8, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{t('performance.metrics.riskRewardRatio')}</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#ec4899', fontFamily: 'monospace' }}>{risk_reward_ratio?.toFixed(2) || '—'}</Typography>
        </Box>
      </Box>

      {/* Outliers */}
      <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#f59e0b', 0.15)}`, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.2 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChart sx={{ fontSize: 15, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{t('performance.metrics.outliers')}</Typography>
        </Box>
        {[
          { label: t('performance.metrics.winners'), sub: t('performance.metrics.winnersDesc'), val: big_winners, clr: '#10b981' },
          { label: t('performance.metrics.losers'), sub: t('performance.metrics.losersDesc'), val: big_losers, clr: '#ef4444' },
          { label: t('performance.metrics.total'), sub: t('performance.metrics.totalDesc'), val: total_signals, clr: '#3b82f6' },
        ].map(o => (
          <Box key={o.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{o.label}</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{o.sub}</Typography>
            </Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: o.clr, fontFamily: 'monospace' }}>{o.val || 0}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Summary Metrics (for 30/90d tiles row) ──
const SummaryRow = ({ metrics, t }) => {
  if (!metrics || metrics.total_signals === 0) return null;
  const { total_signals, win_rate_4w, avg_return_4w, risk_reward_ratio } = metrics;
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{t('performance.totalSignals')}</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, fontFamily: 'monospace', color: '#1e293b' }}>{total_signals}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{t('performance.winRate4w')}</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, fontFamily: 'monospace', color: win_rate_4w >= 50 ? '#10b981' : '#ef4444' }}>{win_rate_4w?.toFixed(1) || '—'}%</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{t('performance.avgReturn4w')}</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, fontFamily: 'monospace', color: avg_return_4w > 0 ? '#10b981' : '#ef4444' }}>{fmtPct(avg_return_4w)}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{t('performance.riskReward')}</Typography>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, fontFamily: 'monospace', color: '#1e293b' }}>{risk_reward_ratio?.toFixed(2) || '—'}</Typography>
      </Box>
    </Box>
  );
};

// ── Recent Signals Table ──
const RecentSignalsPanel = ({ signals, t }) => {
  const [expanded, setExpanded] = useState(new Set());
  if (!signals || signals.length === 0) {
    return <EmptyState Icon={Assessment} title={t('performance.noData')} subtitle={t('performance.noDataDescription')} />;
  }

  const grouped = signals.reduce((acc, s) => { (acc[s.ticker] = acc[s.ticker] || []).push(s); return acc; }, {});
  const groups = Object.entries(grouped).map(([ticker, sigs]) => {
    const sorted = sigs.sort((a, b) => new Date(b.signal_date) - new Date(a.signal_date));
    return { ticker, latest: sorted[0], all: sorted, count: sorted.length };
  }).sort((a, b) => new Date(b.latest.signal_date) - new Date(a.latest.signal_date));

  const toggle = tk => setExpanded(p => { const n = new Set(p); n.has(tk) ? n.delete(tk) : n.add(tk); return n; });

  const RetCell = ({ v }) => v == null
    ? <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', fontStyle: 'italic' }}>{t('performance.table.pending')}</Typography>
    : <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: v < 0 ? '#ef4444' : '#10b981', fontFamily: 'monospace' }}>{fmtPct(v)}</Typography>;

  const ResultCell = ({ v }) => v == null
    ? <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', fontStyle: 'italic' }}>{t('performance.table.pending')}</Typography>
    : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'center' }}>
        {v ? <Check sx={{ fontSize: 16, color: '#10b981' }} /> : <Close sx={{ fontSize: 16, color: '#ef4444' }} />}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: v ? '#10b981' : '#ef4444' }}>{v ? t('performance.table.correct') : t('performance.table.wrong')}</Typography>
      </Box>;

  const SigRow = ({ sig, isMain, isExp, cnt }) => {
    const canExpand = cnt > 1 && isMain;
    const isDist = sig.signal_type === SIGNAL_TYPES.DISTRIBUTION;
    return (
      <TableRow
        key={`${sig.ticker}-${sig.signal_date}`}
        onClick={() => canExpand && toggle(sig.ticker)}
        sx={{
          cursor: canExpand ? 'pointer' : 'default',
          borderLeft: isMain ? `3px solid ${isDist ? '#ef4444' : '#10b981'}` : 'none',
          bgcolor: isMain ? 'transparent' : '#fafbfc',
          '&:hover': { bgcolor: isMain ? alpha(isDist ? '#ef4444' : '#10b981', 0.03) : '#f1f5f9' },
        }}
      >
        <TableCell sx={{ ...tdSx, py: isMain ? 0.8 : 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: isMain ? 0 : 3 }}>
            {canExpand && (isExp ? <KeyboardArrowDown sx={{ fontSize: 16, color: '#94a3b8' }} /> : <KeyboardArrowRight sx={{ fontSize: 16, color: '#94a3b8' }} />)}
            <Typography sx={{ fontWeight: isMain ? 800 : 500, fontSize: isMain ? '0.84rem' : '0.78rem', color: isMain ? '#1e293b' : '#94a3b8' }}>{sig.ticker}</Typography>
            {isMain && cnt > 1 && (
              <Box sx={{ px: 0.5, py: 0.1, borderRadius: 1, bgcolor: '#059669', minWidth: 18, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', fontFamily: 'monospace' }}>{cnt}</Typography>
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ ...tdSx, color: isMain ? '#475569' : '#94a3b8' }}>{new Date(sig.signal_date).toLocaleDateString('vi-VN')}</TableCell>
        <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isDist ? '#ef4444' : '#10b981' }}>{isDist ? t('performance.signalTypes.distribution') : t('performance.signalTypes.accumulation')}</Typography></TableCell>
        <TableCell align="right" sx={tdSx}><Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: sig.score >= 80 ? '#10b981' : '#64748b', fontFamily: 'monospace' }}>{sig.score}</Typography></TableCell>
        <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{sig.entry_price.toLocaleString('vi-VN')}</TableCell>
        <TableCell align="right" sx={tdSx}><RetCell v={sig.outcome_1w} /></TableCell>
        <TableCell align="right" sx={tdSx}><RetCell v={sig.outcome_4w} /></TableCell>
        <TableCell align="right" sx={tdSx}><RetCell v={sig.outcome_8w} /></TableCell>
        <TableCell align="center" sx={tdSx}><ResultCell v={sig.is_correct_4w} /></TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Explanation */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', bgcolor: alpha('#3b82f6', 0.02) }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.6, mb: 0.5 }}>{t('performance.recentDescription')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#ef4444' }}>{t('performance.distributionLabel')}</Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>— {t('performance.distributionExplanation')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981' }}>{t('performance.accumulationLabel')}</Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>— {t('performance.accumulationExplanation')}</Typography>
          </Box>
        </Box>
      </Box>
      {/* Table */}
      <TableContainer sx={{ maxHeight: 540 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>{t('performance.table.ticker')}</TableCell>
              <TableCell sx={thSx}>{t('performance.table.date')}</TableCell>
              <TableCell sx={thSx}>{t('performance.table.type')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('performance.table.score')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('performance.table.entryPrice')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('performance.table.oneWeek')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('performance.table.fourWeeks')}</TableCell>
              <TableCell align="right" sx={thSx}>{t('performance.table.eightWeeks')}</TableCell>
              <TableCell align="center" sx={thSx}>{t('performance.table.result')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(({ ticker, latest, all, count }) => {
              const isExp = expanded.has(ticker);
              return (
                <React.Fragment key={ticker}>
                  <SigRow sig={latest} isMain isExp={isExp} cnt={count} />
                  {isExp && all.slice(1).map(s => <SigRow key={`${s.ticker}-${s.signal_date}`} sig={s} cnt={count} />)}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ══════════════════════════════════════
const PerformanceDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState(null);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 5 * 60 * 1000); return () => clearInterval(i); }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const r = await performanceAPI.getDashboard();
      setData(r.data || r);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const triggerScan = async () => {
    try {
      setScanning(true); setScanMsg(null);
      await performanceAPI.triggerScanner();
      setScanMsg({ ok: true, text: t('performance.scannerTriggered', 'Scanner triggered! Refresh in 1-2 minutes.') });
      setTimeout(() => { fetchData(); setScanMsg(null); }, 90000);
    } catch (e) { setScanMsg({ ok: false, text: e.message }); }
    finally { setScanning(false); }
  };

  const TABS = [
    { key: 'dist', label: t('performance.tabs.distribution'), color: '#ef4444', Icon: TrendingDown },
    { key: 'accum', label: t('performance.tabs.accumulation'), color: '#10b981', Icon: TrendingUp },
    { key: 'recent', label: t('performance.tabs.recent'), color: '#3b82f6', Icon: Assessment },
  ];

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#3b82f6', 0.3)}`,
          }}>
            <BarChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{t('performance.title')}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>{t('performance.description')}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small"
            startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : <ShowChart sx={{ fontSize: 16 }} />}
            onClick={triggerScan} disabled={scanning}
            sx={{ px: 2, py: 0.7, fontSize: '0.82rem', fontWeight: 700, textTransform: 'none', borderRadius: 2, borderColor: '#e2e8f0', color: '#64748b' }}>
            {scanning ? t('performance.scanning', 'Scanning...') : t('performance.runScanner', 'Scan Now')}
          </Button>
          <Button variant="contained" size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            onClick={fetchData} disabled={loading}
            sx={{
              px: 2, py: 0.7, fontSize: '0.82rem', fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: `0 3px 8px ${alpha('#3b82f6', 0.3)}`,
              textTransform: 'none', borderRadius: 2,
            }}>
            {t('performance.refresh', 'Refresh')}
          </Button>
        </Box>
      </Box>

      {/* Scan message */}
      {scanMsg && (
        <Box sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1.5, bgcolor: alpha(scanMsg.ok ? '#10b981' : '#ef4444', 0.06), border: `1px solid ${alpha(scanMsg.ok ? '#10b981' : '#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: scanMsg.ok ? '#10b981' : '#ef4444' }}>{scanMsg.text}</Typography>
        </Box>
      )}

      {/* Error */}
      {error && !data && (
        <Box sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
        </Box>
      )}

      {/* Skeleton or content */}
      {loading && !data ? <DashboardSkeleton /> : data && (
        <>
          {/* Summary tiles */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            {/* 30-day */}
            <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#667eea', 0.15)}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#667eea', mb: 1 }}>{t('performance.last30Days')}</Typography>
              <SummaryRow metrics={data.last_30_days} t={t} />
            </Box>
            {/* 90-day */}
            <Box sx={{ borderRadius: 2, border: `1px solid ${alpha('#ec4899', 0.15)}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#ec4899', mb: 1 }}>{t('performance.last90Days')}</Typography>
              <SummaryRow metrics={data.last_90_days} t={t} />
            </Box>
          </Box>

          {/* Tab bar */}
          <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
            {TABS.map((tab, idx) => {
              const active = activeTab === idx;
              return (
                <Box key={tab.key} onClick={() => setActiveTab(idx)} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8, px: 2.5, py: 1.2, cursor: 'pointer',
                  borderBottom: active ? `2.5px solid ${tab.color}` : '2.5px solid transparent', mb: '-2px',
                  transition: 'all 0.15s', '&:hover': { bgcolor: alpha(tab.color, 0.04) },
                }}>
                  <tab.Icon sx={{ fontSize: 18, color: active ? tab.color : '#94a3b8' }} />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: active ? 800 : 600, color: active ? tab.color : '#64748b' }}>{tab.label}</Typography>
                </Box>
              );
            })}
          </Box>

          {/* Tab content */}
          <Box sx={{ borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
            {activeTab === 0 && <DetailedMetrics metrics={data.distribution_metrics} signalType={SIGNAL_TYPES.DISTRIBUTION} t={t} />}
            {activeTab === 1 && <DetailedMetrics metrics={data.accumulation_metrics} signalType={SIGNAL_TYPES.ACCUMULATION} t={t} />}
            {activeTab === 2 && <RecentSignalsPanel signals={data.recent_signals} t={t} />}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PerformanceDashboard;
